import React from "react"
import { v4 as uuid } from "uuid"

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core"
import {
  Attribute,
  AttributeType,
  AttributeTypeNames,
  attributeTypes,
} from "@root/store/types"
import AttributesTable from "./AttributesTable"
import SnackContext from "./Snackbar"

export interface Props {
  attributes: Attribute[]
  onSetAttribute(attribute: Attribute): void
  onRemoveAttribute(attribute: Attribute): void
}

const useStyles = makeStyles({
  button: {
    alignSelf: "center",
  },
})

const InputTypeForAttribute: { [K in AttributeType]: string } = {
  date: "date",
  text: "text",
  number: "number",
}

const Attributes: React.FC<Props> = ({
  attributes,
  onSetAttribute,
  onRemoveAttribute,
}) => {
  const [key, setKey] = React.useState("")
  const [value, setValue] = React.useState("")
  const [type, setType] = React.useState<AttributeType>("text")
  const { createSnack } = React.useContext(SnackContext)
  const classes = useStyles()

  return (
    <>
      <AttributesTable
        attributes={attributes}
        onRemoveAttribute={onRemoveAttribute}
      />

      <Typography variant="h6">Add New</Typography>
      <Box
        component="form"
        data-qa-selector="attributes-form"
        onSubmit={(ev) => {
          ev.preventDefault()
          try {
            onSetAttribute(makeAttribute(type, key, value))
            setValue("")
          } catch (e) {
            createSnack(e.message)
          }
        }}
      >
        <Grid container spacing={2}>
          <Grid item sm={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="attribute-type-label">Type</InputLabel>
              <Select
                data-qa-selector="type-select"
                labelId="attribute-type-label"
                id="attribute-type-id"
                value={type}
                onChange={(ev) => setType(ev.target.value as AttributeType)}
              >
                {attributeTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {AttributeTypeNames[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              required
              data-qa-selector="key-input"
              id="key-id"
              label="Key"
              value={key}
              onChange={(ev) => setKey(ev.target.value)}
            />
          </Grid>

          <Grid item sm={4} xs={12}>
            <TextField
              required
              data-qa-selector="value-input"
              id="value-id"
              label="Value"
              type={InputTypeForAttribute[type]}
              value={value}
              onChange={(ev) => setValue(ev.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item sm={1} xs={12} className={classes.button}>
            <Button
              data-qa-selector="submit"
              type="submit"
              variant="contained"
              disabled={!key || !value || !type}
              fullWidth
            >
              Set
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default Attributes

export function makeAttribute(
  type: AttributeType,
  key: string,
  value: string
): Attribute {
  const id = uuid()
  switch (type) {
    case "date":
      const dt = new Date(value)
      if (Number.isNaN(dt.getTime())) {
        throw new Error("unable to parse date")
      }
      const dtString = dt.toISOString().split(".")[0]
      if (!dtString) {
        throw new Error("unable to format date")
      }
      return {
        id,
        type: "date",
        key,
        value: dtString,
      }
    case "number":
      const num = Number(value)
      if (Number.isNaN(num)) {
        throw new Error("unable to parse number")
      }
      return {
        id,
        type: "number",
        key,
        value: num,
      }
    case "text":
      return {
        id,
        type: "text",
        key,
        value,
      }
    default:
      const msg: never = type
      throw new Error(`invalid type: ${msg}`)
  }
}
